import { Coupon } from "./coupons";
import { LoyaltyBonus } from "./loyalty";

export enum OrderAggregatorService {
  talabat = "talabat",
}

export enum OrderType {
  INSTANT_ORDER = "instantOrder",
  SCHEDULE_ORDER = "scheduleOrder",
  SCHEDULE_COLD_ORDER = "scheduleColdOrder",
}

export enum OrderTypeLabel {
  instantOrder = "Instant Order",
  scheduleOrder = "Schedule Order",
  scheduleColdOrder = "Schedule Cold Order",
}

export enum OrderStatus {
  PENDING = "pending",
  ACCEPTED = "accepted",
  PREPARING = "preparing",
  PREPARED = "prepared",
  READY_FOR_PICKUP = "ready_for_pickup",
  IN_DELIVERY = "in_delivery",
  DELIVERED = "delivered",
  AUTO_FINALIZED = "auto_finalized",
  CANCELED = "canceled",
  SCHEDULED = "scheduled",
}

export enum OrderStatusLabel {
  pending = "New",
  accepted = "Accepted",
  preparing = "Preparing",
  prepared = "Prepared",
  ready_for_pickup = "Ready for pickup",
  in_delivery = "In delivery",
  delivered = "Delivered",
  auto_finalized = "Auto finalized",
  canceled = "Canceled",
  scheduled = "Scheduled",
}

export enum ChargeStatus {
  PENDING = "pending",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
  REFUND_REQUESTED = "refund_requested",
  REFUND_SUCCEEDED = "refund_succeeded",
  REFUND_FAILED = "refund_failed",
  UNPAID = "unpaid",
}

export enum ChargeStatusLabel {
  pending = "Pending",
  succeeded = "Succeeded",
  failed = "Failed",
  refund_requested = "Refund requested",
  refund_succeeded = "Refund succeeded",
  refund_failed = "Refund failed",
  unpaid = "Unpaid",
}

export enum ChargeFailureReasonLabel {
  insufficient_funds = "insufficient funds",
  expired_balance = "expired balance",
  refund_failed = "refund_failed",
  internal_error = "internal_error",
}

export enum EOrderTypeValue {
  PICK_UP = "1",
  DELIVERY = "2",
  EAT_IN = "3",
}

export enum EOrderTypeLabel {
  PICK_UP = "Pick Up",
  DELIVERY = "Delivery",
  EAT_IN = "Eat In",
}

export enum EPaymentMethod {
  CARD = "Card",
  CASH = "Cash",
}

export interface OrderStatusHistoryItem {
  previousStatus: string | null;
  newStatus: "pending" | string;
  timestamp: string;
}

export interface OrderParams {
  offset?: number;
  limit?: number;
  sortBy?: string;
  sortingOrder?: string;
}

export enum OrderFilterTypes {
  sidLike = "sidLike",
  externalOrderId = "externalOrderId",
  service = "service",
  locationId = "locationId",
  phoneNumber = "phoneNumber",
  orderStatus = "orderStatus",
  chargeStatus = "chargeStatus",
  scheduledAfter = "scheduledAtAfter",
  deliveredAfter = "deliverAtAfter",
  createdAfter = "createdAtAfter",
  updatedAfter = "updatedAtAfter",
  createdAtRange = "dates",
}

export enum OrderFilterNames {
  sidLike = "Order ID",
  externalOrderId = "Aggregator Order ID",
  service = "Order Source",
  phoneNumber = "Phone number",
  orderStatus = "Order status",
  chargeStatus = "Charge status",
  createdAtAfter = "Created after",
  updatedAtAfter = "Updated after",
  scheduledAtAfter = "Scheduled after",
  locationId = "Location",
  deliverAtAfter = "Delivered after",
  dates = "Created At",
}

export type OrderStatusDto =
  | "pending"
  | "accepted"
  | "preparing"
  | "prepared"
  | "ready_for_pickup"
  | "in_delivery"
  | "delivered"
  | "auto_finalized"
  | "canceled";

export type ChargeStatusDto =
  | "pending"
  | "succeeded"
  | "failed"
  | "refund_requested"
  | "refund_succeeded"
  | "refund_failed"
  | "unpaid";

export type ChargeFailureReasonDto =
  | "insufficient_funds"
  | "expired_balance"
  | "refund_failed"
  | "internal_error"
  | null;

export type CustomerAddressResponseDto = {
  id: string;
  name: string;
  lng: number;
  lat: number;
  city: string;
  area: string;
  block: string;
  street: string;
  building: string;
  floor: string | null;
  flat: string | null;
  phone: string;
  additionalInfo: string | null;
  isSelected: boolean;
  isInAnyDeliveryZone?: boolean;
  createdAt: string;
  updatedAt: string;
};

export type AddonsModifiers = {
  productId: string;
  productPLU: string;
  productName: string;
  productDescription: string;
  productCalories: number;
  quantity: number;
  fmcCentsPrice: number;
  fiatCentsPrice: number;
};

export type OrderItemResponseDto = {
  productId: string;
  productPLU: string;
  productName: string;
  imageURL?: string | null;
  quantity: number;
  fmcCentsPrice: number;
  fiatCentsPrice: number;
  remark: string;
  subItems: AddonsModifiers[];
};

export type ScheduledOrderItemResponseDto = {
  isSnoozed: boolean;
  productId: string;
  imageURL?: string | null;
  productPLU: string;
  productName: {
    en: string;
    ar: string;
  };
  productDescription: string;
  productCalories: number;
  quantity: number;
  isVip: boolean;
  fmcCentsPrice: number;
  fiatCentsPrice: number;
  remark: string;
  subItems: AddonsModifiers[];
};

export type AggregatorOrderItemResponseDto = {
  productId: string;
  productPLU: string;
  productDescription: string;
  productName: {
    en: string | null;
    ar: string | null;
  };
  imageURL?: string;
  productCalories: number;
  quantity: number;
  nutritionInfo: {
    carbs: number;
    protein: number;
    fat: number;
  };
  fmcCentsPrice: number;
  fiatCentsPrice: number;
  remark: string;
  subItems: AddonsModifiers[];
  service: string;
};

export type DeliveryResponseDto = {
  id: string;
  customerId: string;
  locationId: string;
  deliveryFeeFiatCents: number;
  deliveryFeeFmcCents: number;
  deliveryAddress: CustomerAddressResponseDto;
  deliverectValidationId: string | null;
  deliveryProvider: string;
  expiresAt: string;
  estimatedDeliveryTime: string | null;
  estimatedDeliveryTimeEnd: string | null;
  orderId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type OrderStatusHistoryItemResponseDto = {
  previousStatus: OrderStatusDto | null;
  newStatus: OrderStatusDto;
  timestamp: string;
};

export enum AllPaymentTypes {
  All = "all",
}

export enum OrderPaymentType {
  FiatCents = "fiatCents",
  FmcCents = "fmcCents",
}

export enum OrderPaymentTypeLabel {
  fiatCents = "KWD",
  fmcCents = "Coin",
}

export type OrderResponseDto = {
  paymentType: OrderPaymentType;
  id: string;
  sid: string;
  menuId: string;
  locationId: string;
  locationName: string | null;
  deliveryId: string;
  customerId: string;
  firstName: string | null;
  lastName: string | null;
  fullName?: string | null;
  phoneNumber: string | null;
  delivery: DeliveryResponseDto;
  items: OrderItemResponseDto[];
  totalFiatCentsPrice: number;
  totalFmcCentsPrice: number;
  orderStatus: OrderStatusDto;
  chargeStatus: ChargeStatusDto;
  coupons: Coupon[];
  chargeFailureReason: ChargeFailureReasonDto;
  orderStatusHistory: OrderStatusHistoryItemResponseDto[];
  chargeStatusHistory: OrderStatusHistoryItemResponseDto[];
  timeslot: string;
  createdAt: string;
  updatedAt: string;
  type: OrderType;
};

export type ScheduledOrderResponseDto = {
  isOutOfStock?: boolean;
  deliveredAt?: string | null;
  id: string;
  createdAt: string;
  updatedAt: string;
  sid: string;
  customerId: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName?: string | null;
  phoneNumber: string | null;
  paymentType: OrderPaymentType;
  menuId: string;
  locationId: string;
  locationName: string;
  coupons: Coupon[];
  items: ScheduledOrderItemResponseDto[];
  totalFiatCentsPrice: number | null;
  totalFmcCentsPrice: number | null;
  totalFiatCentsPriceOriginal: number | null;
  totalFmcCentsPriceOriginal: number | null;
  totalKwdDiscount: number | null;
  deletedAt: string | null;
  orderStatus: OrderStatusDto;
  chargeStatus: ChargeStatusDto;
  type: OrderType;
  chargeFailureReason: null;
  orderStatusHistory: OrderStatusHistoryItemResponseDto[];
  chargeStatusHistory: OrderStatusHistoryItemResponseDto[];
  expectedPreorderDeliveryTime: string;
  cutlery: boolean;
  timeslot: string;
  deliveryProvider: "deliverect" | "own";
  delivery: DeliveryResponseDto;
};

export type AggregatorOrderResponseDto = {
  id: string;
  sid: string;
  menuId: string;
  locationId: string;
  locationName: string;
  note: string;
  firstName: string;
  phoneNumber: string;
  items: AggregatorOrderItemResponseDto[];
  totalFiatCentsPrice: number;
  totalFmcCentsPrice: number;
  createdAt: string;
  updatedAt: string;
  orderId: string;
  hashTag: string;
  service: string;
};

export type GetOrdersResponseDto = {
  totalCount: number;
  items: OrderResponseDto[];
};

export type GetScheduledOrdersResponseDto = {
  totalCount: number;
  items: ScheduledOrderResponseDto[];
};

export type GetAggregatorOrdersResponseDto = {
  totalCount: number;
  items: AggregatorOrderResponseDto[];
};

export type ScheduleOrdersSettingsDto = {
  isScheduleOrders: boolean;
  orderCancelTime: number;
  scheduleOrderTime: number;
  orderAppearingTime: number;
  timeToNextSlot: number;
};

export type UserDeliveryAddressForm = {
  id: string;
  name: string;
  city: string;
  area: string;
  block: string;
  street: string;
  building: string;
  floor: string;
  flat: string;
  phoneCode: string;
  phone: string;
  additionalInfo: string;
  isSelected?: boolean;
};

export type CreateOrderDeliveryManualRequestDto = {
  customerId: string;
  locationId: string;
  deliveryProvider: string;
  address: {
    street: string;
    area: string;
    block: string;
    building: string;
    city: string;
    phoneNumber: string;
    lng?: number;
    lat?: number;
  };
};

export type CreateOrderDeliveryManualResponseDto = {
  id: string;
  customerId: string;
  locationId: string;
  deliveryFeeFiatCents: number;
  deliveryFeeFmcCents: number;
  deliveryAddress: {
    id: string;
    name: string;
    lng: number;
    lat: number;
    city: string;
    area: string;
    block: string;
    street: string;
    building: string;
    floor: string;
    flat: string;
    phone: string;
    additionalInfo: string;
    isSelected: boolean;
    isInAnyDeliveryZone: boolean;
    createdAt: string;
    updatedAt: string;
  };
  deliverectValidationId: string;
  deliveryProvider: string;
  expiresAt: string;
  estimatedDeliveryTime: string;
  estimatedDeliveryTimeEnd: string;
  orderId: string;
  createdAt: string;
  updatedAt: string;
};

export type CreateOrderRequestDto = {
  customerId?: string;
  menuId: string;
  items: {
    productId: string;
    quantity: number;
    fmcCentsPrice: number;
    remark?: string;
    subItems: {
      productId: string;
      quantity: number;
      fmcCentsPrice: number;
      fiatCentsPrice: number;
      remark?: string;
    }[];
  }[];
  totalFmcCentsPrice: number;
  totalFiatCentsPrice: number;
  deliveryId: string;
  expectedPreorderDeliveryTime?: string;
  expectedPreorderDeliveryTimeEnd?: string;
  cutlery: boolean;
  timeslot?: string;
  deliveryFeeFmcCentsPrice: number;
  deliveryFeeFiatCentsPrice: number;
  paymentType: OrderPaymentType;
  type: OrderType;
  couponIds: string[];
  loyaltyDiscount: number;
  loyaltyBonus: (LoyaltyBonus & { levelName: string }) | null;
  totalFiatCentsPriceOriginal: number;
  totalFmcCentsPriceOriginal: number;
};

export type CreateOrderResponseDto = {
  id: string;
  sid: string;
  coupons: {
    id: string;
    createdAt: string;
    updatedAt: string;
    discountOff: number;
    discountType: string;
    expiryDate: string;
    startDate: string;
    orderPaymentType: string;
    numberOfUses: 0;
    minimumOrderFiatCentsPrice: number;
    minimumOrderFmcCentsPrice: number;
    couponCode: string;
    couponType: string;
    isActive: boolean;
    allowMultipleUsage: boolean;
    allowedUsers: {
      id: string;
      phoneNumber: string;
      firstName: string;
      lastName: string;
      email: string;
    }[];
  };
  menuId: string;
  locationId: string;
  locationName: string;
  deliveryId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  paymentType: string;
  type: string;
  customerId: string;
  delivery: {
    id: string;
    customerId: string;
    locationId: string;
    deliveryFeeFiatCents: number;
    deliveryFeeFmcCents: number;
    deliveryAddress: {
      id: string;
      name: string;
      lng: number;
      lat: number;
      city: string;
      area: string;
      block: string;
      street: string;
      building: string;
      floor: string;
      flat: string;
      phone: string;
      additionalInfo: string;
      isSelected: boolean;
      isInAnyDeliveryZone: boolean;
      createdAt: string;
      updatedAt: string;
    };
    deliverectValidationId: string;
    deliveryProvider: string;
    expiresAt: string;
    estimatedDeliveryTime: string;
    estimatedDeliveryTimeEnd: string;
    orderId: string;
    createdAt: string;
    updatedAt: string;
  };
  items: {
    productId: string;
    productPLU: string;
    productDescription: string;
    productName: string;
    imageURL: string;
    productCalories: string;
    quantity: number;
    fmcCentsPrice: number;
    fiatCentsPrice: number;
    remark: string;
    subItems: {
      productId: string;
      productPLU: string;
      productName: string;
      productDescription: string;
      productCalories: number;
      quantity: number;
      fmcCentsPrice: number;
      fiatCentsPrice: number;
    }[];
  }[];
  totalFiatCentsPrice: number;
  totalFmcCentsPrice: number;
  orderStatus: string;
  chargeStatus: string;
  chargeFailureReason: string;
  orderStatusHistory: {
    previousStatus: string;
    newStatus: string;
    timestamp: string;
  }[];
  chargeStatusHistory: {
    previousStatus: string;
    newStatus: string;
    timestamp: string;
  }[];
  cutlery: boolean;
  hasRating: boolean;
  timeslot: string;
  createdAt: string;
  updatedAt: string;
};

export type CreateAggregatorOrderRequestDto = {
  menuId: string;
  locationId: string;
  items: {
    productId: string;
    quantity: number;
    fmcCentsPrice: number;
    remark?: string;
    subItems: {
      productId: string;
      quantity: number;
      fmcCentsPrice: number;
      remark?: string;
    }[];
  }[];
  cutlery: boolean;
  totalFmcCentsPrice: number;
  totalFiatCentsPrice: number;
  phoneNumber: string;
  firstName: string;
  service: string;
  orderId: string;
  hashTag: string;
};
